import { useCallback } from 'react'

import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { useEditingStateWithCtx } from '@/atoms'
import { RmAccountFormValues } from '../../types'
import { updateSessionUser } from '@/features/domain/user'
import { useTexts } from '../../hooks/useTexts'

export function useOnSubmit() {
  // track the mounted state of the parent component
  const isUnmounted = useIsUnmounted()
  const dispatch = useAppDispatch()
  const texts = useTexts()
  const toast = useNotification()
  const { resetEditing } = useEditingStateWithCtx('settings')

  const updateRmAccount = useCallback(
    async (formValues: RmAccountFormValues) => {
      try {
        const result = await dispatch(updateSessionUser(formValues))

        if (updateSessionUser.rejected.match(result)) {
          throw new Error(result.payload?.message ?? 'Internal error')
        }
        toast.success(texts.updateAccountSuccess, { autoClose: 5000 })
        if (isUnmounted()) return

        // if the update has been completed successfully let's close the form
        resetEditing()
      } catch (error) {
        toast.error(texts.updateAccountError, { autoClose: 5000 })
        if (isUnmounted()) return
      }
    },
    [dispatch, isUnmounted, resetEditing, toast],
  )

  return useCallback(
    async (formValues: RmAccountFormValues) => {
      await updateRmAccount(formValues)
    },
    [updateRmAccount],
  )
}
